import { useFormik } from "formik";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import { object, string } from "yup";
import ExternalRefIdHelpAdornment from "./ExternalRefIdHelpAdornment";
import { MonitoringFrequency } from "../../types/APIMonitoringFrequency";
import { MonitorType } from "../../types/APIMonitorType";
import { useTrueBizApi } from "../../api";
import APICompanyMatchProfile from "../../types/APICompanyMatchProfile";

const MonitoringCreateFormSchema = object({
  domain: string()
    .test(
      "domain",
      "Enter a valid domain name (e.g. example.com)",
      function (value) {
        if (!value) {
          return false;
        }
        const regexp = new RegExp(
          /^((?!-))(xn--)?[a-z0-9][a-z0-9-_]{0,61}[a-z0-9]{0,1}\.(xn--)?([a-z0-9-]{1,61}|[a-z0-9-]{1,30}\.[a-z]{2,})$/
        );
        return regexp.test(value);
      }
    )
    .required(),
  monitorType: string().oneOf(Object.keys(MonitorType)).required(),
  frequency: string().oneOf(Object.keys(MonitoringFrequency)).required(),
  profileId: string().nullable(),
  externalRefId: string().nullable(),
});
type FormState = ReturnType<typeof MonitoringCreateFormSchema.validateSync>;

export default function MonitorCreateForm({
  open,
  onClose,
  onSubmit,
  domain,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  domain: string | null;
}) {
  const api = useTrueBizApi();

  const [matchProfiles, setMatchProfiles] = useState<
    APICompanyMatchProfile[] | null
  >(null);
  const formik = useFormik({
    initialValues: {
      domain: domain || "",
      frequency: "WEEKLY",
      monitorType: "website_status_monitor",
      profileId: null,
      externalRefId: "",
    },
    onSubmit: async (values: FormState, { resetForm }) => {
      const payload = {
        domain: values.domain,
        frequency: values.frequency,
        profile_id: values.profileId,
        monitor_type: values.monitorType,
        external_ref_id: values.externalRefId || null,
      };

      await api.createMonitor(payload);
      resetForm();
      onSubmit();
    },
    validationSchema: MonitoringCreateFormSchema,
  });

  useEffect(() => {
    const loadProfiles = async () => {
      setMatchProfiles(await api.getCompanyMatchProfiles());
    };

    loadProfiles();
  }, [api, setMatchProfiles, open]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>Create Monitor</DialogTitle>
      <DialogContent>
        {matchProfiles === null ? (
          <CircularProgress size={28} sx={{ marginLeft: 1 }} />
        ) : (
          <Grid container spacing={2} mt={1} pl={3} pr={2}>
            <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
              <Grid item xs={12}>
                <FormControl sx={{ width: "100%" }}>
                  <TextField
                    label="Domain"
                    id="domain-text-field"
                    onChange={formik.handleChange}
                    value={formik.values.domain}
                    name="domain"
                  />
                  {formik.errors.domain && formik.touched.domain && (
                    <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                      {formik.errors.domain}
                    </Alert>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} mt={3}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel id="frequency-label">Frequency</InputLabel>
                  <Select
                    labelId="frequency-label"
                    label="Frequency"
                    id="frequency-select"
                    defaultValue={formik.initialValues.frequency}
                    onChange={formik.handleChange}
                    value={formik.values.frequency}
                    name="frequency"
                  >
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="SEMI_ANNUALLY">Semiannually</MenuItem>
                    <MenuItem value="ANNUALLY">Annually</MenuItem>
                  </Select>
                  {formik.errors.frequency && formik.touched.frequency && (
                    <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                      {formik.errors.frequency}
                    </Alert>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} mt={3}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel id="monitor-type-label">Monitor Type</InputLabel>
                  <Select
                    labelId="monitor-type-label"
                    label="Monitor Type"
                    id="monitor-type-select"
                    defaultValue={formik.initialValues.monitorType}
                    onChange={formik.handleChange}
                    value={formik.values.monitorType}
                    name="monitorType"
                  >
                    <MenuItem value="website_status_monitor">
                      Website Status
                    </MenuItem>
                    <MenuItem value="web_presence_monitor">
                      Web Presence
                    </MenuItem>
                  </Select>
                  {formik.errors.monitorType && formik.touched.monitorType && (
                    <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                      {formik.errors.monitorType}
                    </Alert>
                  )}
                </FormControl>
              </Grid>

              {matchProfiles && !!matchProfiles.length && (
                <Grid item xs={12} mt={3}>
                  <FormControl sx={{ width: "100%" }}>
                    <InputLabel id="match-profile-label" shrink>
                      Risk Profile
                    </InputLabel>
                    <Select
                      labelId="match-profile-label"
                      label="Risk Profile"
                      id="match-profile-select"
                      defaultValue={formik.initialValues.profileId}
                      value={
                        formik.values.profileId ? formik.values.profileId : ""
                      }
                      onChange={formik.handleChange}
                      name="profileId"
                      displayEmpty
                    >
                      <MenuItem value={""}>TrueBiz Default</MenuItem>
                      {matchProfiles?.map((profile) => (
                        <MenuItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.errors.profileId && formik.touched.profileId && (
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        {formik.errors.profileId}
                      </Alert>
                    )}
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} mt={3}>
                <FormControl sx={{ width: "100%" }}>
                  <TextField
                    label="External Reference Id"
                    id="external-ref-id-text-field"
                    onChange={formik.handleChange}
                    value={formik.values.externalRefId}
                    name="externalRefId"
                    InputProps={{
                      endAdornment: <ExternalRefIdHelpAdornment />,
                    }}
                  />
                  {formik.errors.externalRefId &&
                    formik.touched.externalRefId && (
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        {formik.errors.externalRefId}
                      </Alert>
                    )}
                </FormControl>
              </Grid>

              <Grid item xs={12} mt={3}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "row" }}>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={onClose}
                      disabled={formik.isSubmitting}
                    >
                      Cancel
                    </Button>
                  </Box>

                  <Box sx={{ display: "flex" }}>
                    <Button
                      variant="contained"
                      disabled={formik.isSubmitting}
                      type="submit"
                    >
                      Create
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </form>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}
